// Resolve the child's pronoun set for static UI copy in the HFP report.
//
// Mirrors the backend resolution order in hfp_v2_integration._resolve_pronoun_set
// (which personalises the AI narratives server-side with the same set):
//   1. subject.preferredPronouns / subject.pronouns on the patient record
//   2. the guardian's Request for Referral answer (lg_cyp_pronouns)
//   3. gender-neutral they/them
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { PRONOUNS } from './hfpDisplay';

const OPTION_TO_PRONOUN = {
  he_him: 'he/him',
  she_her: 'she/her',
  they_them: 'they/them',
  ze_zir: 'ze/zir',
};

function normaliseSet(value) {
  const v = String(value || '').trim().toLowerCase().replace(/\s+/g, '');
  // Records store either the set form ("she/her") or the referral form's
  // optionId form ("she_her") — the backend accepts both, so mirror it.
  if (PRONOUNS[v]) return v;
  if (OPTION_TO_PRONOUN[v]) return OPTION_TO_PRONOUN[v];
  return null;
}

/**
 * @param {string} orgId
 * @param {string} patientRecordId
 * @param {object|null} record  the patientRecords doc data, if already loaded
 * @returns {Promise<string>} one of the supported pronoun-set keys
 */
export async function resolvePronounSet(orgId, patientRecordId, record = null) {
  const fromRecord =
    normaliseSet(record?.subject?.preferredPronouns) || normaliseSet(record?.subject?.pronouns);
  if (fromRecord) return fromRecord;

  try {
    const base = ['rms_organisations', orgId, 'patientRecords', patientRecordId, 'formInstances'];
    let snap = await getDoc(doc(db, ...base, 'requestForReferral_guardian'));
    if (!snap.exists()) snap = await getDoc(doc(db, ...base, 'requestForReferral'));
    if (!snap.exists()) return 'they/them';

    const answer = snap.data()?.answers?.lg_cyp_pronouns;
    // Mirror the backend's normalisation: the answer may be a list of
    // strings/{optionId} items, a single string, or a single dict.
    const items = Array.isArray(answer) ? answer : answer != null ? [answer] : [];
    for (const item of items) {
      const optionId = typeof item === 'string' ? item : item?.optionId;
      if (OPTION_TO_PRONOUN[optionId]) return OPTION_TO_PRONOUN[optionId];
    }
  } catch (err) {
    // Non-critical: the report reads correctly with they/them.
    console.warn('[hfpReport] Could not resolve pronoun preference:', err);
  }
  return 'they/them';
}
